import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { AppVersion, VersionService } from '../services/version.service';
import { Observable } from 'rxjs';
import { LoginService } from './login.service';

@Component({
    selector: 'ctv-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  // Auth0's redirect otherwise fires as soon as ngOnInit runs, before the browser has even
  // painted this page - anyone stuck looping back here (e.g. an SSO session pinned on an OTP/MFA
  // step-up they can't complete) never gets a chance to see or click the sign-out link below.
  // This delay is the difference between the link existing in the DOM and it being reachable.
  private static readonly AUTO_REDIRECT_DELAY_MS = 3000;

  version$: Observable<AppVersion>;
  private redirectTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(private authService: AuthService,
              private route: ActivatedRoute,
              private versionService: VersionService,
              private loginService: LoginService) {
    this.version$ = this.versionService.getVersion();
  }

  ngOnInit() {
    if (window.document.location.origin.includes('ctvbuyer')) {
      window.document.getElementById('appFavicon').setAttribute('href', 'assets/favicons/favicon-3.png');
      window.document.title = 'CTVBuyer';
    }
    // A stash left by LoginService.logoutWithReturnUrl takes priority over the query param -
    // it reflects the page the user was actually force-logged-out of, whereas a returnUrl already
    // in the query params here would just be wherever AdminGuard sent us after landing back from
    // Auth0's post-logout redirect (e.g. the dashboard), not that original page.
    const storedReturnUrl = sessionStorage.getItem(LoginService.POST_LOGOUT_RETURN_URL_KEY);
    sessionStorage.removeItem(LoginService.POST_LOGOUT_RETURN_URL_KEY);
    // queryParamMap.get() always returns a single string (or null) - unlike queryParams, which
    // returns an array if returnUrl is repeated in the query string, and would otherwise flow an
    // array into Auth0's appState as the redirect target.
    const returnUrl = storedReturnUrl || this.route.snapshot.queryParamMap.get('returnUrl');
    this.redirectTimeoutId = setTimeout(
      () => this.authService.loginWithRedirect(returnUrl),
      LoginComponent.AUTO_REDIRECT_DELAY_MS
    );
  }

  ngOnDestroy() {
    clearTimeout(this.redirectTimeoutId);
  }

  signOut() {
    clearTimeout(this.redirectTimeoutId);
    this.loginService.logout();
  }
}
